import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const STORAGE_KEY = "2hc.b2b.cart.v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const add = (product, qty = 1) => {
    setItems((cur) => {
      const idx = cur.findIndex((c) => c.product_id === product.id);
      const incomingQty = Math.min(20, qty);
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], qty: Math.min(20, next[idx].qty + incomingQty) };
        return next;
      }
      return [...cur, {
        product_id: product.id,
        name: product.name,
        tagline: product.tagline || "",
        image_url: product.image_url || "",
        kind: product.kind,
        price: Number(product.price || 0),
        mrp: Number(product.mrp || 0),
        rx_required: !!product.rx_required,
        partner_name: product.partner_name || "",
        category_name: product.category_name || "",
        qty: incomingQty,
        prescription_id: "",
      }];
    });
    setOpen(true);
  };

  const setQty = (product_id, qty) => {
    setItems((cur) => cur.map((c) => c.product_id === product_id
      ? { ...c, qty: Math.max(1, Math.min(20, qty)) }
      : c));
  };
  const remove = (product_id) => setItems((cur) => cur.filter((c) => c.product_id !== product_id));
  const setPrescription = (product_id, prescription_id) => {
    setItems((cur) => cur.map((c) => c.product_id === product_id ? { ...c, prescription_id } : c));
  };
  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, c) => s + (c.price * c.qty), 0),
    [items]
  );
  const hasRx = useMemo(() => items.some((c) => c.rx_required), [items]);
  const itemCount = useMemo(() => items.reduce((s, c) => s + c.qty, 0), [items]);

  return (
    <CartCtx.Provider value={{ items, add, setQty, remove, setPrescription, clear, subtotal, hasRx, itemCount, open, setOpen }}>
      {children}
    </CartCtx.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
